import React from 'react';
import { Card } from './ui/card';
import MarkdownText from './MarkdownText';
import { 
  Building2, 
  ExternalLink, 
  Users, 
  PieChart, 
  Shield, 
  AlertTriangle, 
  FileText,
  Scale,
  Calendar,
  TrendingUp,
  Banknote,
  Layers,
  RefreshCw,
  HelpCircle
} from 'lucide-react';

/**
 * Get funding structure label and icon
 */
const getFundingStructureInfo = (structure) => {
  const structures = {
    equity: { label: 'Equity', icon: TrendingUp, color: 'text-primary' },
    debt: { label: 'Debt', icon: Banknote, color: 'text-accent' },
    hybrid: { label: 'Hybrid', icon: Layers, color: 'text-emerald-400' },
    convertible: { label: 'Convertible Note', icon: RefreshCw, color: 'text-amber-400' },
  };
  return structures[structure] || { label: structure || 'Not specified', icon: FileText, color: 'text-foreground-muted' };
};

/**
 * Sponsor Profile Section
 */
export const SponsorProfileSection = ({ project }) => {
  // Only render if there's sponsor information
  if (!project.sponsor_name && !project.sponsor_description) {
    return null;
  }

  return (
    <div className="mb-6">
      <h3 className="text-base sm:text-lg font-heading font-semibold flex items-center gap-2 mb-4">
        <Building2 className="h-5 w-5 text-primary" />
        Sponsor Profile
      </h3>
      
      <Card className="glass p-4 sm:p-5">
        <div className="flex items-start gap-4">
          {/* Sponsor Logo */}
          {project.sponsor_logo_url && (
            <div className="flex-shrink-0">
              <img 
                src={project.sponsor_logo_url} 
                alt={project.sponsor_name}
                className="w-16 h-16 rounded-xl object-cover bg-white/10"
              />
            </div>
          )}
          
          <div className="flex-1 min-w-0">
            {/* Sponsor Name */}
            <div className="flex items-center gap-2 mb-2">
              <h4 className="text-lg font-semibold">{project.sponsor_name || 'Project Sponsor'}</h4>
              {project.sponsor_website && (
                <a 
                  href={project.sponsor_website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:text-primary-hover transition-colors"
                >
                  <ExternalLink className="h-4 w-4" />
                </a>
              )}
            </div>
            
            {/* Sponsor Description */}
            {project.sponsor_description && (
              <p className="text-sm text-foreground-muted mb-3 leading-relaxed">
                {project.sponsor_description}
              </p>
            )}
            
            {/* Track Record */}
            {project.sponsor_track_record && (
              <div className="mt-3 pt-3 border-t border-white/10">
                <span className="text-xs font-medium text-foreground-muted uppercase tracking-wide">Track Record</span>
                <p className="text-sm text-foreground-muted mt-1">{project.sponsor_track_record}</p>
              </div>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};

/**
 * Capital Stack Section
 */
export const CapitalStackSection = ({ project }) => {
  const fundingInfo = getFundingStructureInfo(project.funding_structure);
  const FundingIcon = fundingInfo.icon;
  
  // Only render if there's capital stack information
  const hasCapitalStack = project.capital_stack_total || project.capital_stack_equity || 
                          project.capital_stack_debt || project.use_of_funds || project.funding_structure;
  
  if (!hasCapitalStack) {
    return null;
  }

  const total = project.capital_stack_total || 0;
  const equity = project.capital_stack_equity || 0;
  const debt = project.capital_stack_debt || 0;
  const equityPct = total > 0 ? (equity / total * 100).toFixed(0) : 0;
  const debtPct = total > 0 ? (debt / total * 100).toFixed(0) : 0;

  return (
    <div className="mb-6">
      <h3 className="text-base sm:text-lg font-heading font-semibold flex items-center gap-2 mb-4">
        <PieChart className="h-5 w-5 text-primary" />
        Capital Stack
      </h3>
      
      <Card className="glass p-4 sm:p-5">
        {/* Funding Structure Badge */}
        {project.funding_structure && (
          <div className="flex items-center gap-2 mb-4">
            <span className="text-xs font-medium text-foreground-muted uppercase tracking-wide">Structure:</span>
            <span className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-white/10 ${fundingInfo.color}`}>
              <FundingIcon className="h-3.5 w-3.5" />
              {fundingInfo.label}
            </span>
          </div>
        )}
        
        {/* Capital Stack Breakdown */}
        {total > 0 && (
          <div className="space-y-3 mb-4">
            <div className="flex justify-between text-sm">
              <span className="text-foreground-muted">Total Project Cost</span>
              <span className="font-semibold">€{total.toLocaleString()}</span>
            </div>
            
            {equity > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-foreground-muted">Equity</span>
                <span className="font-medium text-primary">€{equity.toLocaleString()} ({equityPct}%)</span>
              </div>
            )}
            
            {debt > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-foreground-muted">Debt</span>
                <span className="font-medium text-accent">€{debt.toLocaleString()} ({debtPct}%)</span>
              </div>
            )}
            
            {/* Visual Bar */}
            {(equity > 0 || debt > 0) && (
              <div className="w-full h-3 rounded-full bg-white/10 overflow-hidden flex">
                {equity > 0 && (
                  <div 
                    className="h-full bg-primary" 
                    style={{ width: `${equityPct}%` }}
                  />
                )}
                {debt > 0 && (
                  <div 
                    className="h-full bg-accent" 
                    style={{ width: `${debtPct}%` }}
                  />
                )}
              </div>
            )}
          </div>
        )}
        
        {/* Funding Structure Notes */}
        {project.funding_structure_notes && (
          <p className="text-sm text-foreground-muted mb-4">{project.funding_structure_notes}</p>
        )}
        
        {/* Use of Funds */}
        {project.use_of_funds && (
          <div className="pt-3 border-t border-white/10">
            <span className="text-xs font-medium text-foreground-muted uppercase tracking-wide">Use of Funds</span>
            <div className="mt-2">
              <MarkdownText content={project.use_of_funds} className="text-sm text-foreground-muted" />
            </div>
          </div>
        )}
        
        {/* Capital Stack Notes */}
        {project.capital_stack_notes && (
          <p className="text-xs text-foreground-muted mt-3 italic">{project.capital_stack_notes}</p>
        )}
      </Card>
    </div>
  );
};

/**
 * Governance Section
 */
export const GovernanceSection = ({ project }) => {
  // Only render if there's governance information
  if (!project.governance_rights && !project.reporting_cadence && !project.distribution_logic) {
    return null;
  }

  return (
    <div className="mb-6">
      <h3 className="text-base sm:text-lg font-heading font-semibold flex items-center gap-2 mb-4">
        <Scale className="h-5 w-5 text-primary" />
        Governance & Reporting
      </h3>
      
      <Card className="glass p-4 sm:p-5 space-y-4">
        {/* Reporting Cadence */}
        {project.reporting_cadence && (
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Calendar className="h-4 w-4 text-accent" />
              <span className="text-sm font-medium">Reporting Frequency</span>
            </div>
            <p className="text-sm text-foreground-muted capitalize">{project.reporting_cadence.replace('_', ' ')}</p>
            {project.reporting_cadence_notes && (
              <p className="text-xs text-foreground-muted mt-1">{project.reporting_cadence_notes}</p>
            )}
          </div>
        )}
        
        {/* Distribution Logic */}
        {project.distribution_logic && (
          <div className="pt-3 border-t border-white/10">
            <div className="flex items-center gap-2 mb-1">
              <Banknote className="h-4 w-4 text-accent" />
              <span className="text-sm font-medium">Distribution Logic</span>
            </div>
            <p className="text-sm text-foreground-muted">{project.distribution_logic}</p>
          </div>
        )}
        
        {/* Governance Rights */}
        {project.governance_rights && (
          <div className="pt-3 border-t border-white/10">
            <div className="flex items-center gap-2 mb-1">
              <Users className="h-4 w-4 text-accent" />
              <span className="text-sm font-medium">Investor Rights</span>
            </div>
            <MarkdownText content={project.governance_rights} className="text-sm text-foreground-muted" />
          </div>
        )}
      </Card>
    </div>
  );
};

/**
 * Risk Factors Section
 */
export const RiskFactorsSection = ({ project }) => {
  return (
    <section
      className="bg-white rounded-2xl border border-black/[0.05] px-5 py-5 md:px-7 md:py-6"
      data-testid="risk-factors-section"
    >
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-[17px] w-[17px] text-primary" />
        <h3 className="text-sm md:text-base font-heading">Risk Factors</h3>
      </div>

      {/* Standard disclaimer - full width */}
      <div className="mt-5 md:mt-6 pt-5 md:pt-6 border-t border-black/[0.06]">
        <p className="text-[10px] md:text-[11px] uppercase tracking-[0.16em] text-foreground-muted">
          Important disclaimer
        </p>
        <p className="text-xs md:text-sm text-foreground-muted leading-relaxed mt-1.5">
          Property investments carry risk, including the partial or total loss of the capital invested. Projected
          returns are targets, not guarantees, and actual performance depends on execution, timing, financing and
          market conditions. Real estate is illiquid: there is no assurance that an investment can be sold or exited at
          a given time or price. Past performance is not indicative of future results. The information on this page
          concerns a specific project and does not constitute investment, legal or tax advice. Invest only what you can
          afford to lose and consider seeking independent advice.
        </p>
      </div>

      {(project.risk_factors || project.risk_mitigants) && (
        <div className="mt-6 pt-5 border-t border-black/[0.06] grid gap-6 md:grid-cols-2 md:gap-10">
          <div>
            <p className="text-[10px] md:text-[11px] uppercase tracking-[0.16em] text-foreground-muted">Key risks</p>
            <div className="mt-2">
              {project.risk_factors ? (
                <MarkdownText content={project.risk_factors} className="text-xs md:text-sm text-foreground-muted leading-relaxed" />
              ) : (
                <p className="text-xs md:text-sm text-foreground-muted">Not disclosed for this project.</p>
              )}
            </div>
          </div>
          <div>
            <p className="text-[10px] md:text-[11px] uppercase tracking-[0.16em] text-foreground-muted">Risk mitigants</p>
            <div className="mt-2">
              {project.risk_mitigants ? (
                <MarkdownText content={project.risk_mitigants} className="text-xs md:text-sm text-foreground-muted leading-relaxed" />
              ) : (
                <p className="text-xs md:text-sm text-foreground-muted">Not disclosed for this project.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

/**
 * Exit Strategy Section
 */
export const ExitStrategySection = ({ project }) => {
  // Only render if there's exit information
  if (!project.exit_strategy && !project.liquidity_notes) {
    return null;
  }

  return (
    <div className="mb-6">
      <h3 className="text-base sm:text-lg font-heading font-semibold flex items-center gap-2 mb-4">
        <TrendingUp className="h-5 w-5 text-primary" />
        Exit & Liquidity
      </h3>
      
      <Card className="glass p-4 sm:p-5">
        {/* Exit Strategy */}
        {project.exit_strategy && (
          <div className="mb-4">
            <span className="text-xs font-medium text-foreground-muted uppercase tracking-wide">Exit Strategy</span>
            <div className="mt-2">
              <MarkdownText content={project.exit_strategy} className="text-sm text-foreground-muted" />
            </div>
          </div>
        )}
        
        {/* Liquidity Notes */}
        {project.liquidity_notes && (
          <div className="pt-3 border-t border-white/10">
            <span className="text-xs font-medium text-foreground-muted uppercase tracking-wide">Liquidity Expectations</span>
            <p className="text-sm text-foreground-muted mt-2">{project.liquidity_notes}</p>
          </div>
        )}
      </Card>
    </div>
  );
};

/**
 * Project FAQ Section
 */
export const ProjectFAQSection = ({ project }) => {
  // Only render if there's FAQ content
  if (!project.faq_content) {
    return null;
  }

  return (
    <div className="mb-6">
      <h3 className="text-base sm:text-lg font-heading font-semibold flex items-center gap-2 mb-4">
        <HelpCircle className="h-5 w-5 text-primary" />
        Frequently Asked Questions
      </h3>
      
      <Card className="glass p-4 sm:p-5">
        <MarkdownText content={project.faq_content} className="text-sm text-foreground-muted" />
      </Card>
    </div>
  );
};

/**
 * Compliance Notice Section
 */
export const ComplianceNoticeSection = ({ project }) => {
  // Only render if there's compliance notes
  if (!project.compliance_notes) {
    return null;
  }

  return (
    <div className="mb-6">
      <h3 className="text-base sm:text-lg font-heading font-semibold flex items-center gap-2 mb-4">
        <Shield className="h-5 w-5 text-primary" />
        Compliance Notice
      </h3>
      
      <Card className="glass p-4 sm:p-5 bg-primary/5 border-primary/20">
        <p className="text-sm text-foreground-muted">{project.compliance_notes}</p>
      </Card>
    </div>
  );
};
